import { FC, memo, useContext, useMemo } from "react"

import { css, Theme } from "@emotion/react"
import { Slide, Snackbar } from "@material-ui/core"

import { Button } from "../../components/buttons"
import { Box } from "../../components/layout"
import { Icon, Paragraph } from "../../components/typography"
import { ToastContext } from "./toast-context"

export const Toaster: FC = memo(() => {
	const toastContext = useContext(ToastContext)

	if (!toastContext) {
		throw new Error("Toast must be used within a ToastProvider")
	}

	const [toastState, setToastState] = toastContext
	const variant = toastState?.variant ?? "default"
	const config = VARIANT_CONFIG[variant] ?? VARIANT_CONFIG.default

	const message = useMemo(() => {
		if (!toastState) {
			return null
		}

		const { message: messageText, iconName, iconColor, iconFamily } = toastState

		return (
			<Box display="flex" columnGap={0.75} alignItems="center">
				{!!iconName && (
					<Box css={config.iconWrapStyle} mt={config.iconWrapStyle ? 0 : 0.05}>
						<Icon
							icon={iconName}
							color={config.iconWrapStyle ? undefined : iconColor}
							family={iconFamily}
						/>
					</Box>
				)}
				<Paragraph color={config.iconWrapStyle ? undefined : "white"}>
					{messageText}
				</Paragraph>
			</Box>
		)
	}, [toastState, config])

	const action = useMemo(() => {
		if (!toastState?.action) {
			return undefined
		}

		const { actionText, actionOnClick, actionIcon } = toastState.action

		const handleClick = () => {
			setToastState(null)
			actionOnClick()
		}

		return (
			<Button onClick={handleClick} trailingIcon={actionIcon} containerProps={{ mt: -0.2 }}>
				{actionText}
			</Button>
		)
	}, [toastState?.action, setToastState])

	return (
		<Snackbar
			autoHideDuration={6000}
			message={message}
			open={!!message}
			anchorOrigin={{
				...config.anchorOrigin,
				horizontal: toastState?.anchorHorizontal ?? config.anchorOrigin.horizontal,
			}}
			action={action}
			TransitionComponent={Slide}
			key={toastState?.message}
			onClose={(_e, reason) => {
				// For some reason the clickaway is firing unexpectedly.
				// STFU, clickaway.
				if (reason !== "clickaway") {
					setToastState(null)
				}
			}}
			css={config.css}
		/>
	)
})

const snackbarStyles = (theme: Theme) => css`
	.MuiSnackbarContent-root {
		background: ${theme.palette.primary.dark};
	}
	.MuiSnackbarContent-action {
		padding-right: 1rem;
		button {
			.button-text {
				color: white;
				font-size: 1rem;
				font-weight: bold;
			}
			svg {
				color: white;
				font-size: 1rem;
			}
		}
	}
`

const lightSnackbarStyles = (theme: Theme) => css`
	.MuiSnackbarContent-root {
		background: ${theme.palette.colors.sky200};
		border-radius: 8px;
		color: ${theme.palette.colors.black500};
		position: relative;

		:before {
			content: " ";
			background: ${theme.palette.colors.sky500};
			width: 6px;
			height: 100%;
			position: absolute;
			top: 0;
			left: 0;
			border-radius: 8px 0 0 8px;
		}
	}
	.MuiSnackbarContent-message {
		padding: 4px 0;
		p {
			font-size: 14px;
			font-weight: 700;
			line-height: 24px;
			margin: 0;
		}
	}
`

const iconCircleStyle = css`
	background: white;
	border-radius: 50%;
	width: 2.5rem;
	height: 2.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	svg {
		color: #0b75e1;
	}
`

const VARIANT_CONFIG: Record<
	string,
	{
		anchorOrigin: { vertical: "top" | "bottom"; horizontal: "left" | "center" | "right" }
		iconWrapStyle: ReturnType<typeof css> | undefined
		css: (theme: Theme) => ReturnType<typeof css> | (ReturnType<typeof css> | false)[]
	}
> = {
	default: {
		anchorOrigin: { vertical: "top", horizontal: "center" },
		iconWrapStyle: undefined,
		css: (theme) => snackbarStyles(theme),
	},
	light: {
		anchorOrigin: { vertical: "top", horizontal: "center" },
		iconWrapStyle: iconCircleStyle,
		css: (theme) => [snackbarStyles(theme), lightSnackbarStyles(theme)],
	},
}
